/**
 * Esquemas de escritura del dominio Propiedades.
 *
 * Se usan tal cual en los dos lados: `parse()` en la ruta de la API y `zodResolver()` en el
 * formulario de la web. Una regla como «la fecha de fin es posterior a la de inicio» se
 * escribe UNA vez.
 */
import { z } from 'zod';
import {
  zCadastralReference,
  zIsoDate,
  zLatitude,
  zLongitude,
  zMoney,
  zMoneyPositive,
  zOptionalText,
  zPostalCode,
  zText,
  zUuid,
} from '../validation/primitives';

export const PROPERTY_TYPES = ['piso', 'casa', 'local', 'edificio', 'habitaciones'] as const;
export const PROPERTY_STATUSES = ['activa', 'en_reforma', 'vendida'] as const;
export const UNIT_TYPES = ['habitacion', 'apartamento', 'completo'] as const;
export const UNIT_STATUSES = ['libre', 'ocupada', 'no_disponible'] as const;
export const ENERGY_RATINGS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;
export const GEOCODE_PRECISIONS = [
  'exacta',
  'calle',
  'municipio',
  'manual',
  'sin_geocodificar',
] as const;
export const VALUATION_SOURCES = ['ia', 'tasacion', 'manual', 'portal'] as const;
export const RATE_TYPES = ['fija', 'variable', 'mixta'] as const;
export const EXPENSE_FREQUENCIES = ['mensual', 'trimestral', 'anual'] as const;

// ---------------------------------------------------------------------------
// Sociedad
// ---------------------------------------------------------------------------

export const createPortfolioSchema = z.object({
  name: zText(120, 'El nombre'),
  description: zOptionalText(500),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});

export const updatePortfolioSchema = createPortfolioSchema.partial();

// ---------------------------------------------------------------------------
// Unidad
// ---------------------------------------------------------------------------

export const unitInputSchema = z.object({
  code: zText(40, 'El código de la unidad'),
  unitType: z.enum(UNIT_TYPES),
  areaM2: z.number().min(1).max(100_000).nullable().optional(),
  bedrooms: z.number().int().min(0).max(50).nullable().optional(),
  bathrooms: z.number().int().min(0).max(50).nullable().optional(),
  baseRent: zMoneyPositive,
  floor: zOptionalText(20),
  orientation: zOptionalText(30),
  hasPrivateBathroom: z.boolean().optional(),
  furnished: z.boolean().optional(),
  notes: zOptionalText(1000),
  status: z.enum(UNIT_STATUSES).optional(),
});

export const createUnitSchema = unitInputSchema;

/**
 * En la edición `status` no se acepta: la ocupación la fija el ciclo de vida del contrato
 * (activar entrega `ocupada`, rescindir entrega `libre`). Dejar que el usuario la escriba a
 * mano permite marcar «libre» una unidad con contrato activo y descuadrar los KPI de golpe.
 */
export const updateUnitSchema = unitInputSchema.omit({ status: true }).partial();

/**
 * Modo de explotación elegido en el paso 3 del alta. Es lo que traduce «un piso entero» o
 * «5 habitaciones» a filas de `units`, que es de donde salen la ocupación y los KPI.
 */
export const unitPlanSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('entera'),
    baseRent: zMoneyPositive,
  }),
  /**
   * Por habitaciones. Cada una con SU renta: en un piso compartido la exterior con baño no
   * vale lo mismo que la interior, y ponerles a todas la misma cifra obligaba a entrar una por
   * una a corregirla justo después de crearlas.
   *
   * El número de habitaciones NO viaja: es `rents.length`. Mandar las dos cosas permite que se
   * contradigan, y entonces hay que decidir cuál manda.
   */
  z.object({
    mode: z.literal('habitaciones'),
    rents: z
      .array(zMoneyPositive)
      .min(1, 'Al menos una habitación.')
      .max(50, 'Máximo 50 habitaciones.'),
  }),
  z.object({
    mode: z.literal('manual'),
    units: z.array(unitInputSchema).min(1, 'Añade al menos una unidad.').max(50),
  }),
  /** Sin unidades todavía: se admite, pero la UI avisa de que no se puede alquilar. */
  z.object({ mode: z.literal('ninguna') }),
]);

// ---------------------------------------------------------------------------
// Inmueble
// ---------------------------------------------------------------------------

/**
 * Una línea de los costes de compra: notaría, registro, ITP, gestoría, comisión… El importe
 * va como texto decimal, igual que el resto del dinero.
 */
export const acquisitionCostItemSchema = z.object({
  concept: zText(80, 'El concepto'),
  amount: zMoneyPositive,
});

const propertyBase = z.object({
  /** Opcional: sin él, la API usa la cartera única de la organización («Cartera principal»). */
  portfolioId: zUuid.optional(),
  name: zText(160, 'El nombre'),
  propertyType: z.enum(PROPERTY_TYPES),
  status: z.enum(PROPERTY_STATUSES).optional(),

  addressLine: zText(240, 'La dirección'),
  floor: zOptionalText(20),
  door: zOptionalText(20),
  city: zText(120, 'La ciudad'),
  province: zOptionalText(120),
  postalCode: zPostalCode.nullable().optional(),
  country: z.string().trim().length(2).toUpperCase().default('ES'),

  latitude: zLatitude.nullable().optional(),
  longitude: zLongitude.nullable().optional(),
  geocodePrecision: z.enum(GEOCODE_PRECISIONS).optional(),

  cadastralReference: zCadastralReference.nullable().optional(),
  builtYear: z
    .number()
    .int()
    .min(1000, 'Año inverosímil.')
    .max(new Date().getUTCFullYear() + 5, 'Ese año todavía no ha llegado.')
    .nullable()
    .optional(),
  areaM2: z.number().min(1).max(1_000_000).nullable().optional(),
  bedrooms: z.number().int().min(0).max(100).nullable().optional(),
  bathrooms: z.number().int().min(0).max(100).nullable().optional(),

  energyRating: z.enum(ENERGY_RATINGS).nullable().optional(),
  energyRatingExpiresAt: zIsoDate.nullable().optional(),

  acquisitionDate: zIsoDate.nullable().optional(),
  acquisitionPrice: zMoneyPositive,
  /**
   * Total de costes. Se acepta por compatibilidad, pero si viene `acquisitionCostItems` la API
   * lo IGNORA y guarda la suma de las líneas: un total que no cuadra con su desglose es peor
   * que no tener desglose.
   */
  acquisitionCosts: zMoneyPositive.optional(),
  acquisitionCostItems: z
    .array(acquisitionCostItemSchema)
    .max(40, 'Como mucho 40 costes por inmueble.')
    .optional(),
  currentMarketValue: zMoneyPositive.optional(),

  notes: zOptionalText(4000),
  /** Enseñar la pestaña «Financiación» en la ficha. */
  financingEnabled: z.boolean().optional(),
});

/**
 * El certificado energético caduca a los 10 años; una fecha de caducidad sin calificación es
 * un dato huérfano que luego genera una alerta de algo que no existe.
 */
function energyCoherent(value: {
  energyRating?: unknown;
  energyRatingExpiresAt?: unknown;
}, ctx: z.RefinementCtx): void {
  if (value.energyRatingExpiresAt && !value.energyRating) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['energyRating'],
      message: 'Indica la calificación energética si informas su caducidad.',
    });
  }
}

export const createPropertySchema = propertyBase
  .extend({
    /** Unidades creadas en el mismo paso. Ver `unitPlanSchema`. */
    unitPlan: unitPlanSchema.optional(),
  })
  .superRefine(energyCoherent);

export const updatePropertySchema = propertyBase.partial().superRefine(energyCoherent);

/** Cambio de estado con su propia regla de negocio (ver `services/properties.ts`). */
export const changePropertyStatusSchema = z.object({
  status: z.enum(PROPERTY_STATUSES),
  reason: zOptionalText(500),
});

// ---------------------------------------------------------------------------
// Valoración, financiación y gastos
// ---------------------------------------------------------------------------

export const createValuationSchema = z.object({
  valuedAt: zIsoDate,
  value: zMoneyPositive,
  source: z.enum(VALUATION_SOURCES),
  confidence: z.number().min(0).max(1).nullable().optional(),
  notes: zOptionalText(1000),
});

function datesOrdered(
  value: { startDate?: string | null; endDate?: string | null },
  ctx: z.RefinementCtx,
): void {
  if (value.startDate && value.endDate && value.endDate < value.startDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['endDate'],
      message: 'La fecha de fin debe ser posterior a la de inicio.',
    });
  }
}

const loanBase = z.object({
  lender: zText(160, 'La entidad'),
  principalAmount: zMoneyPositive,
  outstandingBalance: zMoneyPositive,
  /** Tipo nominal anual en %. `Decimal(6,4)` en la base: 2,9 % es `2.9`. */
  interestRate: z.number().min(0).max(30),
  rateType: z.enum(RATE_TYPES),
  startDate: zIsoDate,
  endDate: zIsoDate.nullable().optional(),
  monthlyPayment: zMoneyPositive,
});

export const createLoanSchema = loanBase.superRefine((value, ctx) => {
  datesOrdered(value, ctx);
  if (Number(value.outstandingBalance) > Number(value.principalAmount)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['outstandingBalance'],
      message: 'El saldo pendiente no puede superar al capital concedido.',
    });
  }
});

export const updateLoanSchema = loanBase.partial().superRefine(datesOrdered);

const insuranceBase = z.object({
  insurer: zText(160, 'La aseguradora'),
  policyNumber: zOptionalText(80),
  coverageType: zOptionalText(120),
  premium: zMoneyPositive,
  startDate: zIsoDate,
  endDate: zIsoDate,
});

export const createInsuranceSchema = insuranceBase.superRefine(datesOrdered);
export const updateInsuranceSchema = insuranceBase.partial().superRefine(datesOrdered);

const expenseBase = z.object({
  concept: zText(160, 'El concepto'),
  amount: zMoneyPositive,
  frequency: z.enum(EXPENSE_FREQUENCIES),
  dayOfMonth: z.number().int().min(1).max(28).nullable().optional(),
  startDate: zIsoDate,
  endDate: zIsoDate.nullable().optional(),
  unitId: zUuid.nullable().optional(),
  categoryId: zUuid.nullable().optional(),
  vendorId: zUuid.nullable().optional(),
  active: z.boolean().optional(),
});

export const createExpenseSchema = expenseBase.superRefine(datesOrdered);
export const updateExpenseSchema = expenseBase.partial().superRefine(datesOrdered);

// ---------------------------------------------------------------------------
// Fotos
// ---------------------------------------------------------------------------

export const updatePhotoSchema = z.object({
  caption: zOptionalText(200),
  isCover: z.boolean().optional(),
});

export const reorderPhotosSchema = z.object({
  /** Lista COMPLETA de identificadores en el orden deseado. Por eso el verbo es `PUT`. */
  order: z.array(zUuid).min(1, 'Envía al menos una foto.').max(30),
});

// ---------------------------------------------------------------------------
// Filtros del listado
// ---------------------------------------------------------------------------

export const PROPERTY_SORT_FIELDS = [
  'name',
  'city',
  'marketValue',
  'equity',
  'occupancy',
  'grossYield',
  'monthlyRent',
] as const;

export const propertyListQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  portfolio: zUuid.optional(),
  city: z.string().trim().max(120).optional(),
  status: z.enum(PROPERTY_STATUSES).optional(),
  minValue: zMoney.optional(),
  maxValue: zMoney.optional(),
  sort: z.enum(PROPERTY_SORT_FIELDS).optional(),
  dir: z.enum(['asc', 'desc']).default('desc'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(25),
});

export type CreatePortfolioInput = z.input<typeof createPortfolioSchema>;
export type AcquisitionCostItemInput = z.input<typeof acquisitionCostItemSchema>;
export type CreatePropertyInput = z.input<typeof createPropertySchema>;
export type UpdatePropertyInput = z.input<typeof updatePropertySchema>;
export type UnitInput = z.input<typeof unitInputSchema>;
export type UnitPlanInput = z.input<typeof unitPlanSchema>;
export type CreateValuationInput = z.input<typeof createValuationSchema>;
export type CreateLoanInput = z.input<typeof createLoanSchema>;
export type CreateInsuranceInput = z.input<typeof createInsuranceSchema>;
export type CreateExpenseInput = z.input<typeof createExpenseSchema>;
export type PropertyListQuery = z.input<typeof propertyListQuerySchema>;

// ---------------------------------------------------------------------------
// Escrituras y papeles del inmueble
// ---------------------------------------------------------------------------

export const DEED_KINDS = [
  // Titularidad real
  'compraventa',
  'nota_simple',
  'contrato_compraventa',
  // Impuestos y aspectos económicos
  'ibi',
  'hipoteca',
  // Documentos técnicos y suministros
  'certificado_energetico',
  'cedula_habitabilidad',
  // Seguros y garantías
  'poliza_hogar',
  'garantia_electrodomesticos',
  // Otros
  'planos',
  'anuncio_alquiler',
  'otro',
  // Tipos heredados: siguen siendo válidos para lo ya guardado, pero no se ofrecen al subir.
  'obra_nueva',
  'division_horizontal',
  'herencia',
  'donacion',
] as const;

export type DeedKind = (typeof DEED_KINDS)[number];

/** Etiquetas en el idioma en que la gente los pide, no en el del esquema. */
export const DEED_KIND_LABELS: Record<DeedKind, string> = {
  compraventa: 'Escritura pública',
  nota_simple: 'Nota simple informativa',
  contrato_compraventa: 'Contrato de compraventa',
  ibi: 'IBI',
  hipoteca: 'Hipoteca / Préstamo',
  certificado_energetico: 'CEE',
  cedula_habitabilidad: 'Cédula de habitabilidad / Permiso de primera ocupación',
  poliza_hogar: 'Póliza del seguro del hogar',
  garantia_electrodomesticos: 'Garantías de electrodomésticos',
  planos: 'Planos',
  anuncio_alquiler: 'Anuncio alquiler',
  otro: 'Varios',
  obra_nueva: 'Declaración de obra nueva',
  division_horizontal: 'División horizontal',
  herencia: 'Aceptación de herencia',
  donacion: 'Donación',
};

/**
 * Cómo se agrupa la documentación del inmueble en la interfaz: grupos y, dentro, los tipos
 * de documento que se pueden adjuntar. Es la carpeta tal y como la tiene un propietario.
 */
export interface DeedGroup {
  key: string;
  label: string;
  kinds: readonly DeedKind[];
}

export const DEED_GROUPS: readonly DeedGroup[] = [
  { key: 'titularidad', label: 'Titularidad real', kinds: ['compraventa', 'nota_simple', 'contrato_compraventa'] },
  { key: 'economico', label: 'Impuestos y aspectos económicos', kinds: ['ibi', 'hipoteca'] },
  { key: 'tecnico', label: 'Documentos técnicos y suministros', kinds: ['certificado_energetico', 'cedula_habitabilidad'] },
  { key: 'seguros', label: 'Seguros y garantías', kinds: ['poliza_hogar', 'garantia_electrodomesticos'] },
  { key: 'otros', label: 'Otros', kinds: ['planos', 'anuncio_alquiler', 'otro'] },
];

/**
 * Los datos que acompañan al papel. TODOS opcionales salvo el tipo: en el alta casi nunca se
 * tiene la escritura delante, y exigir el número de protocolo para poder adjuntar el PDF sería
 * garantizar que nadie adjunta nada.
 */
export const deedSchema = z.object({
  kind: z.enum(DEED_KINDS),
  name: zText(200, 'El nombre').optional(),
  notary: zOptionalText(160),
  protocolNumber: zOptionalText(40),
  grantedAt: zIsoDate.nullable().optional(),
  registry: zOptionalText(160),
  volume: zOptionalText(20),
  book: zOptionalText(20),
  page: zOptionalText(20),
  estateNumber: zOptionalText(30),
  notes: zOptionalText(2000),
});

export const updateDeedSchema = deedSchema.partial();

export type DeedInput = z.input<typeof deedSchema>;
export type UpdateDeedInput = z.input<typeof updateDeedSchema>;
